class CmsCache {
  constructor() {
    this.cache = new Map(); // Stores key-value pairs along with their expiration times
  }

  /**
   * Sets a key-value pair in the cache with a time limit.
   * @param {any} key The key to store.
   * @param {any} value The value to store.
   * @param {number} duration The duration in milliseconds for which the item is valid.
   * @returns {boolean} True if the key already existed and was updated, false otherwise.
   */
  async set(key, value, duration) {
    const now = Date.now();
    const expirationTime = now + duration;

    const existingEntry = this.cache.get(key);

    if (existingEntry) {
      // If the key exists, clear its previous expiration timeout
      clearTimeout(existingEntry.timeoutId);
      this.cache.set(key, { value, expirationTime, timeoutId: null }); // Update value and expiration
      this._scheduleExpiration(key, duration); // Reschedule expiration
    } else {
      this.cache.set(key, { value, expirationTime, timeoutId: null });
      this._scheduleExpiration(key, duration);
    }

    return await value;
  }

  /**
   * Retrieves the value associated with a key from the cache.
   * @param {any} key The key to retrieve.
   * @returns {any} The value if found and not expired, otherwise -1.
   */
  get(key) {
    const entry = this.cache.get(key);
    if (!entry) {
      return null; // Key not found
    }

    const now = Date.now();
    if (now < entry.expirationTime) {
      return entry.value; // Valid and not expired
    } else {
      this.cache.delete(key); // Expired, remove from cache
      return null;
    }
  }

  /**
   * Returns the number of unexpired keys in the cache.
   * @returns {number} The count of active keys.
   */
  count() {
    // Iterate and remove expired items to ensure accurate count
    const now = Date.now();
    for (const [key, entry] of this.cache.entries()) {
      if (now >= entry.expirationTime) {
        this.cache.delete(key);
      }
    }
    return this.cache.size;
  }

  /**
   * Schedules the expiration of a key using setTimeout.
   * @param {any} key The key to schedule expiration for.
   * @param {number} duration The duration in milliseconds.
   * @private
   */
  _scheduleExpiration(key, duration) {
    const timeoutId = setTimeout(() => {
      this.cache.delete(key);
    }, duration);

    // Store timeoutId to allow clearing it if the key is updated
    const entry = this.cache.get(key);
    if (entry) {
      entry.timeoutId = timeoutId;
    }
  }
}

export default CmsCache;
